import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { t } from '../../i18n';
import { relativeTime } from '../../format';
import type { Column } from '../../components/ui/table';
import type { SelectOption } from '../../components/ui/select';

interface AuditLog {
  id: string; user_id: string | null; action: string;
  entity_type: string | null; entity_id: string | null;
  details: string | null; ip_address: string | null; user_agent: string | null;
  created_at: string; actor_name: string | null; actor_email: string | null;
}
interface AuditPage { logs: AuditLog[]; total: number; limit: number; offset: number; }
interface Options { actions: string[]; entityTypes: string[]; users: { id: string; name: string | null; email: string | null }[]; }

interface Filters { action: string; entity_type: string; user_id: string; from: string; to: string; q: string; }

/** Humanize a dotted action key, e.g. "student.level_changed" → "student · level changed". */
function humanizeAction(a: string): string {
  return a.split('.').map((p) => p.replace(/_/g, ' ')).join(' · ');
}

/** A readable full timestamp from a UTC SQLite datetime string. */
function fullTime(value: string): string {
  const iso = value.includes('T') ? value : value.replace(' ', 'T') + 'Z';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/** <cadence-audit-page> — read-only, filterable, paginated audit log viewer. */
@customElement('cadence-audit-page')
export class CadenceAuditPage extends LitElement {
  @state() private logs: AuditLog[] = [];
  @state() private total = 0;
  @state() private limit = 50;
  @state() private offset = 0;
  @state() private loading = true;
  @state() private options: Options = { actions: [], entityTypes: [], users: [] };
  @state() private filters: Filters = { action: '', entity_type: '', user_id: '', from: '', to: '', q: '' };
  @state() private selected: AuditLog | null = null;
  private searchTimer?: ReturnType<typeof setTimeout>;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 var(--space-1); font-size: 1.6rem; }
    .lede { color: var(--color-text-secondary); margin: 0 0 var(--space-4); }
    .filters { display: grid; gap: 10px; margin-bottom: var(--space-4); }
    .grid { display: grid; gap: 10px; grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); align-items: end; }
    .search-row { display: flex; gap: 10px; align-items: end; }
    .search-row cadence-input { flex: 1; }
    .clear { white-space: nowrap; }
    .actor-sys { color: var(--color-text-secondary); font-style: italic; }
    .action-badge { font-family: var(--font-mono, monospace); font-size: 0.82rem; }
    .entity { color: var(--color-text-secondary); font-size: 0.85rem; }
    .mono { font-family: var(--font-mono, monospace); }
    .pager { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: var(--space-4); flex-wrap: wrap; }
    .pager .count { color: var(--color-text-secondary); font-size: 0.88rem; }
    .pager .btns { display: flex; gap: 8px; }
    /* Detail modal */
    .detail { display: grid; gap: 12px; }
    .detail dl { display: grid; grid-template-columns: 120px 1fr; gap: 6px 14px; margin: 0; }
    .detail dt { color: var(--color-text-secondary); font-size: 0.85rem; }
    .detail dd { margin: 0; word-break: break-word; }
    pre { background: var(--color-bg-secondary); border: 1px solid var(--color-border); border-radius: var(--radius-md); padding: 12px; overflow-x: auto; font-size: 0.82rem; margin: 0; max-height: 40vh; }
  `;

  connectedCallback(): void {
    super.connectedCallback();
    this.loadOptions();
    this.load();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  private async loadOptions() {
    try { this.options = await api.get<Options>('/audit-logs/options'); }
    catch { /* lacks settings.view — page won't load anyway */ }
  }

  private query(): string {
    const p = new URLSearchParams();
    const f = this.filters;
    if (f.action) p.set('action', f.action);
    if (f.entity_type) p.set('entity_type', f.entity_type);
    if (f.user_id) p.set('user_id', f.user_id);
    if (f.from) p.set('from', f.from);
    if (f.to) p.set('to', f.to);
    if (f.q) p.set('q', f.q);
    p.set('limit', String(this.limit));
    p.set('offset', String(this.offset));
    return p.toString();
  }

  private async load() {
    this.loading = true;
    try {
      const res = await api.get<AuditPage>(`/audit-logs?${this.query()}`);
      this.logs = res.logs; this.total = res.total; this.limit = res.limit; this.offset = res.offset;
    } finally { this.loading = false; }
  }

  /** Set a filter, reset to the first page, and reload (search is debounced). */
  private setFilter(key: keyof Filters, value: string, debounce = false) {
    this.filters = { ...this.filters, [key]: value };
    this.offset = 0;
    if (debounce) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.load(), 300);
    } else {
      this.load();
    }
  }

  private clearFilters() {
    this.filters = { action: '', entity_type: '', user_id: '', from: '', to: '', q: '' };
    this.offset = 0;
    this.load();
  }

  private page(delta: number) {
    const next = this.offset + delta * this.limit;
    if (next < 0 || next >= this.total) return;
    this.offset = next;
    this.load();
  }

  private get hasFilters(): boolean {
    return Object.values(this.filters).some(Boolean);
  }

  private actorLabel(l: AuditLog) {
    if (l.actor_name) return l.actor_name;
    if (l.actor_email) return l.actor_email;
    if (l.user_id) return html`<span class="mono">${l.user_id}</span>`;
    return html`<span class="actor-sys">${t('common.audit.system')}</span>`;
  }

  private columns(): Column<AuditLog>[] {
    return [
      { key: 'created_at', label: t('common.audit.col.when'),
        render: (l) => html`<span title=${fullTime(l.created_at)}>${relativeTime(l.created_at)}</span>` },
      { key: 'actor', label: t('common.audit.col.actor'), render: (l) => this.actorLabel(l) },
      { key: 'action', label: t('common.audit.col.action'),
        render: (l) => html`<span class="action-badge">${humanizeAction(l.action)}</span>` },
      { key: 'entity', label: t('common.audit.col.entity'),
        render: (l) => l.entity_type
          ? html`<span class="entity">${l.entity_type}${l.entity_id ? html` <span class="mono">${l.entity_id}</span>` : ''}</span>`
          : '' },
      { key: 'ip_address', label: t('common.audit.col.ip'), render: (l) => html`<span class="mono">${l.ip_address ?? ''}</span>` },
    ];
  }

  private prettyDetails(details: string | null): string {
    if (!details) return '';
    try { return JSON.stringify(JSON.parse(details), null, 2); }
    catch { return details; }
  }

  private renderFilters() {
    const actionOpts: SelectOption[] = this.options.actions.map((a) => ({ value: a, label: humanizeAction(a) }));
    const entityOpts: SelectOption[] = this.options.entityTypes.map((e) => ({ value: e, label: e }));
    const userOpts: SelectOption[] = this.options.users.map((u) => ({ value: u.id, label: u.name || u.email || u.id }));
    return html`
      <div class="filters">
        <div class="search-row">
          <cadence-input
            label=${t('common.audit.filters.search')}
            placeholder=${t('common.audit.searchPlaceholder')}
            .value=${this.filters.q}
            @input=${(e: CustomEvent) => this.setFilter('q', e.detail, true)}></cadence-input>
          ${this.hasFilters ? html`<cadence-button class="clear" variant="ghost" @click=${() => this.clearFilters()}>${t('common.audit.clear')}</cadence-button>` : ''}
        </div>
        <div class="grid">
          <cadence-select label=${t('common.audit.filters.action')} placeholder=${t('common.audit.allActions')}
            .options=${actionOpts} .value=${this.filters.action}
            @change=${(e: CustomEvent) => this.setFilter('action', e.detail)}></cadence-select>
          <cadence-select label=${t('common.audit.filters.entity')} placeholder=${t('common.audit.allEntities')}
            .options=${entityOpts} .value=${this.filters.entity_type}
            @change=${(e: CustomEvent) => this.setFilter('entity_type', e.detail)}></cadence-select>
          <cadence-select label=${t('common.audit.filters.user')} placeholder=${t('common.audit.allUsers')}
            .options=${userOpts} .value=${this.filters.user_id}
            @change=${(e: CustomEvent) => this.setFilter('user_id', e.detail)}></cadence-select>
          <cadence-input label=${t('common.audit.filters.from')} type="date" .value=${this.filters.from}
            @input=${(e: CustomEvent) => this.setFilter('from', e.detail)}></cadence-input>
          <cadence-input label=${t('common.audit.filters.to')} type="date" .value=${this.filters.to}
            @input=${(e: CustomEvent) => this.setFilter('to', e.detail)}></cadence-input>
        </div>
      </div>
    `;
  }

  private renderPager() {
    if (!this.total) return '';
    const from = this.offset + 1;
    const to = Math.min(this.offset + this.limit, this.total);
    return html`
      <div class="pager">
        <span class="count">${t('common.audit.showing', { from, to, total: this.total })}</span>
        <div class="btns">
          <cadence-button variant="secondary" ?disabled=${this.offset === 0} @click=${() => this.page(-1)}>${t('common.audit.prev')}</cadence-button>
          <cadence-button variant="secondary" ?disabled=${to >= this.total} @click=${() => this.page(1)}>${t('common.audit.next')}</cadence-button>
        </div>
      </div>
    `;
  }

  private renderDetail() {
    const l = this.selected;
    if (!l) return '';
    const details = this.prettyDetails(l.details);
    return html`
      <cadence-modal open heading=${t('common.audit.detailTitle')} @close=${() => (this.selected = null)}>
        <div class="detail">
          <dl>
            <dt>${t('common.audit.col.when')}</dt><dd>${fullTime(l.created_at)}</dd>
            <dt>${t('common.audit.col.actor')}</dt><dd>${this.actorLabel(l)}${l.actor_email && l.actor_name ? html` <span class="entity">(${l.actor_email})</span>` : ''}</dd>
            <dt>${t('common.audit.col.action')}</dt><dd><span class="mono">${l.action}</span></dd>
            <dt>${t('common.audit.col.entity')}</dt><dd>${l.entity_type ? html`${l.entity_type} ${l.entity_id ? html`<span class="mono">${l.entity_id}</span>` : ''}` : '—'}</dd>
            <dt>${t('common.audit.col.ip')}</dt><dd class="mono">${l.ip_address ?? '—'}</dd>
            <dt>${t('common.audit.userAgent')}</dt><dd class="entity">${l.user_agent ?? '—'}</dd>
          </dl>
          ${details ? html`<div><div class="entity" style="margin-bottom:6px">${t('common.audit.details')}</div><pre>${details}</pre></div>` : ''}
        </div>
        <cadence-button slot="footer" @click=${() => (this.selected = null)}>${t('common.actions.cancel')}</cadence-button>
      </cadence-modal>
    `;
  }

  render() {
    return html`
      <h1>${t('common.audit.title')}</h1>
      <p class="lede">${t('common.audit.lede')}</p>
      ${this.renderFilters()}
      ${this.loading && !this.logs.length
        ? html`<cadence-loading></cadence-loading>`
        : this.logs.length
          ? html`<cadence-table clickable .columns=${this.columns()} .rows=${this.logs}
              @row-click=${(e: CustomEvent) => (this.selected = e.detail)}></cadence-table>`
          : html`<cadence-empty-state heading=${t('common.audit.empty')} message=${this.hasFilters ? t('common.audit.emptyFiltered') : ''}></cadence-empty-state>`}
      ${this.renderPager()}
      ${this.renderDetail()}
    `;
  }
}
